<template>
  <div class="gmf-achievements gmf-context">
    <template v-if="! hideDescription">
      <h2 class="gmf-header">{{ t('my_achievements_title') }}</h2>
      <p v-if="t('description')" v-text="t('description')"></p>
    </template>
    <DropdownSelect
      v-if="! hideSortSelect"
      v-model="sortingMethod"
      :options="sortingMethods"
      :noBorder="true"
      :placeholderAlternateStyling="true"
      :noPadding="true"
    />
    <div style="clear:both;"></div>
    <div class="gmf-cheevo-grid" v-if="mode === 'grid'">
      <div
        v-for="achievement in achievements"
        :key="achievement.id"
        class="gmf-cheevo"
        :class="getCheevoClasses(achievement)"
        @click="onClick(achievement)"
      >
        <div class="gmf-cheevo-name" v-text="achievement.name"></div>
        <AchievementImage :achievement="achievement" />
        <div v-if="showXp" class="gmf-cheevo-grid-reward">
          <Icon v-if="showRewardIcons" icon="point_star" />
          <div class="gmf-xp" v-text="getXpText(achievement)"></div>
        </div>
        <div class="gmf-cheevo-desc" v-html="md(achievement.description)"></div>
      </div>
    </div>
    <div class="gmf-cheevo-list" v-else>
      <AchievementItem
        v-for="achievement in achievements"
        :key="achievement.id"
        :achievement="achievement"
        :disableDetailsModal="disableDetailsModal"
        @click="onClick(achievement)"
      />
      <div class="gmf-cheevo gmf-cheevo-filler"></div>
      <div class="gmf-cheevo gmf-cheevo-filler"></div>
    </div>
    <InViewport @enter="autoload" rootMargin="150px">
      <div
        ref="loadBtn"
        v-show="hasMoreContent && ! isLoading"
        @click="loadNextPage"
        class="gmf-btn gmf-btn-primary gmf-btn-sm"
        v-text="t('show_more')"
      />
    </InViewport>
  </div>
</template>

<style>
.gmf-cheevo-list {
  display: flex;
  gap: var(--gmf-object-spacing);
  flex-wrap: wrap;
}

.gmf-cheevo-chevron-right {
  background-image: url(https://cdn.gamifiera.com/static/icons/chevron-right.svg);
  width: 10px;
  height: 20px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-grow: 0;
  flex-shrink: 0;
  opacity: 0.3;
}

.gmf-achievements .gmf-dropdown {
  margin-bottom: var(--gmf-object-spacing);
}
.gmf-cheevo-grid {
  width: 102%;
  margin-left: -1%;
  margin-right: -1%;
  display: flex;
  flex-wrap: wrap;
}
.gmf-cheevo-grid .gmf-cheevo {
  margin: 1%;
  padding: 3% 1%;
  width: 23%;
  display: inline-block;
  box-sizing: border-box;
  border-radius: var(--gmf-rounding);
  cursor: pointer;
  transition: background 0.1s linear;

  /* Grid specific */
  text-align: center;

  background: var(--gmf-col-lightgray);
}

.gmf-cheevo-grid-reward {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--gmf-p-1);
  margin-bottom: var(--gmf-p-2);
}

.gmf-achievements .gmf-btn { /* Load more button */
  margin-top: 16px;
  text-align: center;
}
@media (hover: hover) {
  .gmf-cheevo-grid .gmf-cheevo.gmf-clickable:hover, .gmf-cheevo-list .gmf-cheevo.gmf-clickable:hover {
    background: var(--gmf-col-lightgray);
  }
}

.gmf-cheevo-grid .gmf-achievement-image {
  width: 55%;
  margin: auto;
  margin-top: 8px;
  margin-bottom: 8px;
}
.gmf-cheevo-grid .gmf-achievement-image::after {
  content: "";
  display: block;
  padding-bottom: 100%;
}
.gmf-cheevo-grid .gmf-cheevo-name {
  font-size: 16px;
  font-weight: bold;
  color: var(--gmf-col-black);
}
.gmf-cheevo-grid .gmf-cheevo-desc {
  font-size: 12px;
  color: var(--gmf-col-darkgray);
}

.gmf-cheevo object {
  pointer-events: none;
}

/* Tablet */
@media (max-width: 768px) {
  .gmf-cheevo-grid .gmf-cheevo {
    width: 31.3%;
  }
}

/* Mobile */
@media (max-width: 480px) {
  .gmf-achievements h1 {
    font-size: 24px;
  }
  .gmf-cheevo-grid .gmf-cheevo {
    width: 46%;
    margin: 2%;
    padding: 4% 2%;
  }
  .gmf-cheevo-grid .gmf-cheevo-name {
    font-size: 14px;
  }
  .gmf-cheevo-grid {
    width: 104%;
    margin-left: -2%;
    margin-right: -2%;
  }
  .gmf-cheevo-grid .gmf-achievement-image {
    width: 70%;
  }

  .gmf-modal-body {
    position: relative;
    padding: 16px 18px;
  }
}
</style>

<script>
import md from '@/utils/md';
import callApi from '@/utils/callApi';
import modal from '@/utils/modal';
import state from '@/state';
import DropdownSelect from '@/components/DropdownSelect';
import showAchievementDetails from '@/widgets/AchievementDetails/popup';
import InViewport from '@/components/InViewport';
import AchievementItem from './AchievementItem';
import AchievementImage from '@/components/AchievementImage.vue';
import Icon from '@/components/Icon';

const t = state.t.prefix('achievements');

export default {
  name: 'AchievementList',
  props: {
    modeOverride: {
      Type: String,
      default: '',
    },
    hideDescription: {
      Type: Boolean,
      deafult: true,
    },
    hideSortSelect: {
      Type: Boolean,
      default: false,
    },
    disableDetailsModal: {
      Type: Boolean,
      default: false,
    },
    displayMode: {
      Type: String,
      default: '',
    },
    /**
     * Provide customer id here to load achievements for another customer,
     * instead of the one currently logged in.
     */
    customerId: {
      Type: String,
      default: null,
    },
  },
  components: {
    DropdownSelect,
    InViewport,
    AchievementItem,
    AchievementImage,
    Icon
  },
  data () {
    return {
      state,

      totalAchievementCount: 0,
      achievements: [],
      page: 1,

      isLoading: false,

      autoloadEnabled: true,
      autoloadMargin: 300,

      itemsPerPage: 12,

      sortingMethod: null,

      showXp: true,

      config: {},
    };
  },
  mounted () {
    const mc = this.state.merchantConfig.achievements;
    if (mc && mc.defaultSortMethod) {
      // @TODO check that it is a valid sort method
      this.sortingMethod = mc.defaultSortMethod;
    } else {
      this.sortingMethod = this.state.methis.sortingMethods[0].value;
    }
    this.loadNextPage();
  },
  computed: {
    sortingMethods () {
        return [
        {
          label: t('last_unlocked'),
          value: 'last_unlocked',
        },
        {
          label: t('nearest_unlock'),
          value: 'nearest_unlock',
        },
        {
          label: t('last_updated'),
          value: 'last_updated',
        },
        {
          label: t('name'),
          value: 'name_asc',
        },
      ];
    },
    mode () {
      if (this.modeOverride) {
        return this.modeOverride;
      }
      if (this.displayMode) {
        return this.displayMode;
      }
      return 'grid';
    },
    hasMoreContent () {
      return this.totalAchievementCount >= (this.page-1) * this.itemsPerPage;
    },
    showRewardIcons () {
      return state.merchantConfig.achievements?.showRewardIcons || false;
    },
  },
  watch: {
    sortingMethod () { // @TODO prevent this from being f**** by a parallel loadNextPage
      this.page = 1;
      this.achievements = [];
      this.loadNextPage();
    },
  },
  methods: {
    getXpText (achievement) {
      const r = achievement.newRewards || achievement.rewards;
      const xp = r.xp || r.ip;

      if (typeof xp === 'object') {
        return xp.quantity + state.t('xp');
      }

      // Xp is a number (legacy format for backwards compatibility)
      if (typeof xp === 'number') {
        return xp + state.t('xp');
      }

      return 0 + state.t('xp');
    },
    md,
    onClick (achievement) {
      if (this.disableDetailsModal) {
        return;
      }
      showAchievementDetails(achievement, this.customerId);
    },
    t,
    getCheevoClasses(achievement) {
      const classes = {
        'gmf-cheevo-achieved': achievement.achievedAt > 0,
        'gmf-clickable': ! this.disableDetailsModal,
      };

      if (achievement.tier) {
        classes['gmf-cheevo-tier-' + achievement.tier.level] = true;
      }

      return classes;
    },
    handleEvent (eventName) {
      if (this.config.on && this.config.on[eventName]) {
        return this.config.on[eventName]();
      }
      return true;
    },
    autoload () {
      if (! this.hasMoreContent) {
        return;
      }
      this.loadNextPage();
    },
    loadIfInViewport () {
      if (! this.hasMoreContent) {
        return;
      }
      const r = this.$refs.loadBtn.getBoundingClientRect();
      if (r.height <= 16) {
        return;
      }
      if (r.top - this.autoloadMargin <= (window.innerHeight || document.documentElement.clientHeight)) {
        this.loadNextPage();
      }
    },
    loadNextPage () {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;

      let url = '/v1/client/customer-achievements' + (this.customerId ? ('/' + this.customerId) : '');
      // Add query
      url += `?take=${ this.itemsPerPage }&skip=${ (this.page-1) * this.itemsPerPage }`;
      if (this.customerId) {
        const myProfile = state.profile;
        if (! myProfile || myProfile.id != this.customerId) {
          url += '&filter=unlocked';
        }
      }
      url += '&stack_unlocked';
      // url += '&stack_locked';
      url += '&hide_locked_children';

      if (this.sortingMethod) {
        url += '&sort=' + this.sortingMethod;
      }

      callApi('GET', url)
      .then(res => {
        if (res.status == 401) {
          this.page++;
          this.handleEvent('session_expired') && modal.error(t('session_expired'));
          return;
        }
        res.json().then(data => {
          this.isLoading = false;
          this.totalAchievementCount = data.totalCount;
          this.achievements.push(...data.achievements);

          this.page++;

          if (this.autoloadEnabled) {
            this.$nextTick(()=>{
              this.loadIfInViewport();
            });
          }
        });
      });
    },
  },
};
</script>
